/**
 * Stale-tab recovery. After a deploy the old hashed JS chunks are gone, so a tab
 * opened before the deploy fails to load the next lazy route. Reloading fetches the
 * new index.html and fixes it. We do it automatically, but at most once per window
 * (guarded by a sessionStorage timestamp) so a genuinely broken deploy can never
 * cause a reload loop; after that the user sees the manual "Refresh" UI.
 */

export const CHUNK_RELOAD_KEY = 'twg:chunk-reload-at';
export const CHUNK_RELOAD_COUNT_KEY = 'twg:chunk-reload-count';
export const CHUNK_RELOAD_WINDOW_MS = 60_000;
/** Automatic reloads allowed per tab session; after that the manual Refresh UI is the only path. */
export const CHUNK_RELOAD_MAX_PER_SESSION = 2;
/** Max time we wait for service-worker/cache cleanup before reloading anyway. */
const CLEANUP_TIMEOUT_MS = 2_000;

const CHUNK_MESSAGES = [
  'dynamically imported module', // Chromium / Firefox ("error loading dynamically imported module")
  'Importing a module script failed', // Safari
  'Unable to preload CSS', // Vite CSS preload
];
const WEBPACK_CHUNK_RE = /Loading (CSS )?chunk [\w-]+ failed/;

/** True for dynamic-import / chunk failures. A bare "Failed to fetch" is a network error, not a stale chunk. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (name === 'ChunkLoadError') return true;
  return (
    typeof message === 'string' &&
    (CHUNK_MESSAGES.some((m) => message.includes(m)) || WEBPACK_CHUNK_RE.test(message))
  );
}

/**
 * The PWA service worker precaches the old index.html and chunks, so a plain reload
 * would serve the same stale build. Drop every registration and cache first.
 * Never throws; each step is optional.
 */
async function clearServiceWorkersAndCaches(): Promise<void> {
  try {
    if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* best effort */
  }
  try {
    if (typeof caches !== 'undefined' && caches) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* best effort */
  }
}

/**
 * Reload at most once per window and at most CHUNK_RELOAD_MAX_PER_SESSION times per tab
 * session. Returns true if a reload was scheduled (it happens after SW/cache cleanup, or
 * after a short timeout if cleanup hangs). If sessionStorage is unusable we cannot prove
 * there is no loop, so we do not reload.
 */
export function reloadOnceForStaleChunk(
  now: number = Date.now(),
  reload: () => void = () => window.location.reload()
): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < CHUNK_RELOAD_WINDOW_MS) {
      return false;
    }
    const count = Number(sessionStorage.getItem(CHUNK_RELOAD_COUNT_KEY)) || 0;
    if (count >= CHUNK_RELOAD_MAX_PER_SESSION) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
    sessionStorage.setItem(CHUNK_RELOAD_COUNT_KEY, String(count + 1));
  } catch {
    return false;
  }
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, CLEANUP_TIMEOUT_MS));
  void Promise.race([clearServiceWorkersAndCaches(), timeout]).then(reload);
  return true;
}

/** Vite fires `vite:preloadError` when a dynamic import's preload fails (stale deploy). */
export function installChunkReloadHandler(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Always suppress Vite's default (rethrow), whether or not we reload: if the guard
    // blocks a reload the error still surfaces through the ErrorBoundary's Refresh UI.
    event.preventDefault();
    reloadOnceForStaleChunk();
  });
}
